import { useNavigate, useParams } from "#routers/hooks";
import WorkerVersionInputOutputSchemaFormStep, {
  SchemaType,
} from "#src/batteries-included-components/Panels/FormPanels/CreateWorkerVersionFormPanels/WorkerVersionInputOutputSchemaFormStep";
import WorkerVersionDeploymentInstructionsFormStep from "#src/batteries-included-components/Panels/FormPanels/CreateWorkerVersionFormPanels/WorkerVersionDeploymentInstructionsFormStep";
import WorkerVersionDescriptionFormStep from "#src/batteries-included-components/Panels/FormPanels/CreateWorkerVersionFormPanels/WorkerVersionDescriptionFormStep";
import {
  useCreateOneWorkerVersion,
  useGetOneWorker,
  useListWorkerVersions,
} from "#src/components/hooks/adapters/useWorkers";
import {
  MultiStepFormProvider,
  type MultiStepFormContextType,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { Page, Panel, Pill } from "@validereinc/common-components";
import {
  SortDirection,
  WorkerVersionStatus,
  type CreateOneWorkerVersionResponseType,
  type WorkerVersionCredentials,
  type WorkerVersionStatusType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useMemo, useState } from "react";
import { WorkerDetailRoutePath } from "#src/routes/workers/definitions/[workerId]/detail";
import styles from "./CreateWorkerVersionPage.module.scss";
import { WorkerVersionsCreateRoutePath } from ".";

const cx = classNames.bind(styles);

const CreateWorkerVersionPageContent = ({
  credentials,
  isCreatingWorkerVersion,
}: {
  credentials?: WorkerVersionCredentials;
  isCreatingWorkerVersion: boolean;
}) => {
  const { workerId } = useParams<{ workerId: string }>();
  const [breadcrumbs] = useBreadcrumbsFromRoute(WorkerVersionsCreateRoutePath);
  const { stepper, footer } = useMultiStepFormContext();

  const { data: workerData, isLoading: isLoadingWorker } = useGetOneWorker({
    id: workerId,
  });
  const worker = workerData?.data;

  const { data: workerVersionsData, isLoading: isLoadingWorkerVersions } =
    useListWorkerVersions(
      {
        meta: { id: workerId },
        filters: {},
        sortBy: "version",
        sortDirection: SortDirection.DESCENDING,
      },
      { enabled: Boolean(workerId) }
    );
  const workerVersions = workerVersionsData?.data ?? [];
  const latestWorkerVersion = workerVersions[0];

  const versionNumber = useMemo(() => {
    if (!latestWorkerVersion) return 1;
    const isSchemaLocked = (
      [
        WorkerVersionStatus.PENDING,
        WorkerVersionStatus.FAILED,
      ] as WorkerVersionStatusType[]
    ).includes(latestWorkerVersion.status);
    return isSchemaLocked
      ? latestWorkerVersion.version
      : latestWorkerVersion.version + 1;
  }, [latestWorkerVersion]);

  const isLoading =
    isLoadingWorker || (Boolean(workerId) && isLoadingWorkerVersions);

  return (
    <Page
      title={
        <>
          Create Worker Version&nbsp;
          <Pill variant="default">v{versionNumber}</Pill>
        </>
      }
      category={worker?.name ?? "Worker"}
      isLoading={isLoading}
      shouldCenterContent
      breadcrumbs={breadcrumbs}
      footer={footer}
    >
      <Panel
        className={cx("container")}
        isFluidY={false}
      >
        <div className={cx("stepper-container")}>{stepper}</div>
        <div className={cx("form-container")}>
          {workerId ? (
            <>
              <WorkerVersionDescriptionFormStep stepNumber={1} />
              <WorkerVersionInputOutputSchemaFormStep
                workerId={workerId}
                stepNumber={2}
                schemaType={SchemaType.INPUT}
              />
              <WorkerVersionInputOutputSchemaFormStep
                workerId={workerId}
                stepNumber={3}
                schemaType={SchemaType.OUTPUT}
              />
              <WorkerVersionDeploymentInstructionsFormStep
                stepNumber={4}
                credentials={credentials}
                versionNumber={versionNumber}
                isLoading={isCreatingWorkerVersion}
              />
            </>
          ) : null}
        </div>
      </Panel>
    </Page>
  );
};

export const CreateWorkerVersionPage = () => {
  const { workerId } = useParams<{ workerId: string }>();
  const navigate = useNavigate();

  const [createOneWorkerVersionResponse, setCreateOneWorkerVersionResponse] =
    useState<CreateOneWorkerVersionResponseType | null>(null);

  const createWorkerVersionMutation = useCreateOneWorkerVersion({
    onSuccess: (data) => {
      setCreateOneWorkerVersionResponse(data.data);
    },
    noAlerts: true,
  });

  const onCancel = () => {
    navigate(
      WorkerDetailRoutePath.toLinkParts({
        pathParams: { workerId },
      })
    );
  };

  const onSubmit = (values: MultiStepFormContextType["values"]) => {
    const allValues = values.reduce(
      (total, current) => ({ ...total, ...current }),
      {}
    );

    createWorkerVersionMutation.mutate({
      data: {
        description:
          allValues.description != null && allValues.description !== ""
            ? allValues.description
            : undefined,
        input_schema: allValues.input_schema ?? {},
        output_schema: allValues.output_schema ?? {},
      },
      meta: { id: workerId },
    });
  };

  const onPresentationalStepAction = () => {
    navigate(
      WorkerDetailRoutePath.toLinkParts({
        pathParams: { workerId },
      })
    );
  };

  const credentials = createOneWorkerVersionResponse?.credentials;
  const isCreatingWorkerVersion = createWorkerVersionMutation.isLoading;

  return (
    <MultiStepFormProvider
      steps={[
        {
          label: "Description",
        },
        {
          label: "Input Schema",
        },
        {
          label: "Output Schema",
          nextActionText: "Request Credentials",
        },
        {
          label: "Deployment instructions",
          isPresentational: true,
          nextActionText: "Done",
        },
      ]}
      orientation="vertical"
      onCancel={onCancel}
      onSubmit={onSubmit}
      onPresentationalStepAction={onPresentationalStepAction}
      onSubmitMutationState={{
        isSubmitting: createWorkerVersionMutation.isLoading,
      }}
      stepperClassName={cx("sticky-stepper")}
    >
      <CreateWorkerVersionPageContent
        credentials={credentials}
        isCreatingWorkerVersion={isCreatingWorkerVersion}
      />
    </MultiStepFormProvider>
  );
};

export default CreateWorkerVersionPage;
